/*
  Contact Form section — custom "I have a question about" dropdown.

  Without JavaScript the native <select> stays visible and fully usable, so
  the form keeps working and the browser keeps validating it. When this script
  runs, the native control is painted out (but left in the layout so it is
  still submitted) and a listbox that matches the design takes its place.

  The custom element is registered behind a guard so the section can be used
  more than once on the same page without redeclaring the class.
*/
if (!customElements.get('contact-form-select')) {
  customElements.define(
    'contact-form-select',
    class ContactFormSelect extends HTMLElement {
      connectedCallback() {
        if (this.enhanced) return;

        this.native = this.querySelector('select');
        if (!this.native) return;

        this.enhanced = true;
        this.form = this.closest('form');
        this.field = this.closest('.contact-form__field');
        this.isRequired = this.native.required;
        this.placeholder = this.native.options[0] ? this.native.options[0].textContent.trim() : '';
        this.activeIndex = -1;

        // The native control keeps `required` so contact-form-validate can read
        // it, but it must never take focus while it is painted out.
        this.native.tabIndex = -1;
        this.native.setAttribute('aria-hidden', 'true');

        this.buildToggle();
        this.buildList();
        this.classList.add('is-enhanced');
        this.bindEvents();
        this.syncFromNative();
      }

      buildToggle() {
        const label = this.field ? this.field.querySelector('.contact-form__label') : null;
        this.labelId = label && label.id ? label.id : '';

        this.toggle = document.createElement('button');
        this.toggle.type = 'button';
        this.toggle.className = 'contact-form__select-toggle';
        this.toggle.setAttribute('role', 'combobox');
        this.toggle.setAttribute('aria-haspopup', 'listbox');
        this.toggle.setAttribute('aria-expanded', 'false');
        if (this.labelId) this.toggle.setAttribute('aria-labelledby', this.labelId);
        if (this.isRequired) this.toggle.setAttribute('aria-required', 'true');

        this.value = document.createElement('span');
        this.value.className = 'contact-form__select-value';

        const icon = document.createElement('span');
        icon.className = 'contact-form__select-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML =
          '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>';

        this.toggle.append(this.value, icon);
        this.append(this.toggle);
      }

      buildList() {
        // The panel is what animates; the list itself keeps the design's border.
        this.panel = document.createElement('div');
        this.panel.className = 'contact-form__select-panel';

        this.list = document.createElement('ul');
        this.list.className = 'contact-form__select-list';
        this.list.setAttribute('role', 'listbox');
        // Named by the field's own <label>, the same one the toggle points at.
        if (this.labelId) this.list.setAttribute('aria-labelledby', this.labelId);

        this.options = [];

        Array.from(this.native.options).forEach((option, index) => {
          // Index 0 is the placeholder; it is never offered as a choice.
          if (index === 0 && option.value === '') return;

          const item = document.createElement('li');
          item.className = 'contact-form__select-option';
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', 'false');
          item.tabIndex = -1;
          item.id = `${this.native.id}-option-${index}`;
          item.textContent = option.textContent.trim();
          item.dataset.value = option.value;

          this.list.append(item);
          this.options.push(item);
        });

        this.panel.append(this.list);
        this.append(this.panel);
        if (this.toggle.hasAttribute('aria-controls') === false) {
          this.list.id = `${this.native.id}-listbox`;
          this.toggle.setAttribute('aria-controls', this.list.id);
        }
      }

      bindEvents() {
        this.toggle.addEventListener('click', () => {
          // Opening with the mouse must not pre-highlight a row — the design
          // shows none — so focus only moves into the list for keyboard users.
          this.openedWithKeyboard = false;
          this.setOpen(!this.isOpen);
        });
        this.toggle.addEventListener('keydown', (event) => this.onToggleKeydown(event));
        this.native.addEventListener('change', () => this.syncFromNative());
        this.list.addEventListener('click', (event) => this.onOptionClick(event));
        this.list.addEventListener('keydown', (event) => this.onListKeydown(event));

        this.onDocumentClick = (event) => {
          if (this.isOpen && !this.contains(event.target)) this.setOpen(false);
        };
        document.addEventListener('click', this.onDocumentClick);

        // Keep the open panel the right height when the layout reflows.
        this.onResize = () => {
          if (this.isOpen) this.panel.style.maxHeight = `${this.panel.scrollHeight}px`;
        };
        window.addEventListener('resize', this.onResize);

        if (this.form) {
          this.onReset = () => {
            // The reset is applied after this event, so read the values back
            // on the next task.
            window.setTimeout(() => {
              this.setOpen(false);
              this.syncFromNative();
            }, 0);
          };
          this.form.addEventListener('reset', this.onReset);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onResize);
        if (this.form && this.onReset) this.form.removeEventListener('reset', this.onReset);
      }

      get isOpen() {
        return this.toggle.getAttribute('aria-expanded') === 'true';
      }

      setOpen(open) {
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.classList.toggle('is-open', open);
        this.panel.style.maxHeight = open ? `${this.panel.scrollHeight}px` : '0px';

        if (!open) return;

        const selected = this.options.findIndex((item) => item.getAttribute('aria-selected') === 'true');
        this.activeIndex = selected === -1 ? 0 : selected;

        if (!this.openedWithKeyboard) return;
        // Wait a frame so the panel is visible before an option takes focus.
        window.requestAnimationFrame(() => this.setActive(this.activeIndex));
      }

      setActive(index) {
        if (!this.options.length) return;

        this.activeIndex = Math.max(0, Math.min(index, this.options.length - 1));

        this.options.forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === this.activeIndex);
        });

        // Real focus moves onto the option, so this is the roving-focus pattern;
        // aria-activedescendant belongs to the other one and would contradict it.
        this.options[this.activeIndex].focus();
      }

      select(index) {
        const item = this.options[index];
        if (!item) return;

        this.native.value = item.dataset.value;
        this.native.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncFromNative();
        this.setOpen(false);
        this.toggle.focus();
      }

      syncFromNative() {
        const hasValue = this.native.value !== '';
        const selectedOption = this.native.options[this.native.selectedIndex];

        this.value.textContent = hasValue && selectedOption ? selectedOption.textContent.trim() : this.placeholder;
        this.toggle.dataset.placeholderShown = hasValue ? 'false' : 'true';

        this.options.forEach((item) => {
          item.setAttribute('aria-selected', hasValue && item.dataset.value === this.native.value ? 'true' : 'false');
        });
      }

      onOptionClick(event) {
        const item = event.target.closest('.contact-form__select-option');
        if (!item) return;
        this.select(this.options.indexOf(item));
      }

      onToggleKeydown(event) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.openedWithKeyboard = true;

          if (this.isOpen) {
            this.setActive(this.activeIndex);
          } else {
            this.setOpen(true);
          }
        } else if (event.key === 'Escape') {
          this.setOpen(false);
        }
      }

      onListKeydown(event) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.setActive(this.activeIndex + 1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.setActive(this.activeIndex - 1);
            break;
          case 'Home':
            event.preventDefault();
            this.setActive(0);
            break;
          case 'End':
            event.preventDefault();
            this.setActive(this.options.length - 1);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.select(this.activeIndex);
            break;
          case 'Escape':
            event.preventDefault();
            this.setOpen(false);
            this.toggle.focus();
            break;
          case 'Tab':
            this.setOpen(false);
            break;
          default:
            break;
        }
      }
    },
  );
}

/*
  Form validation, styled the way the design draws it: the offending field gets
  a red border and one red line of copy sits underneath it. Native bubbles are
  switched off here rather than in the markup, so a visitor without JavaScript
  still gets the browser's own validation.
*/
if (!customElements.get('contact-form-validate')) {
  customElements.define(
    'contact-form-validate',
    class ContactFormValidate extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.bound = true;
        this.message = this.dataset.errorMessage || '';
        this.emailMessage = this.dataset.errorEmailMessage || '';
        this.form.setAttribute('novalidate', '');

        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.form.addEventListener('reset', () => {
          window.setTimeout(() => this.clearAll(), 0);
        });
        this.form.addEventListener('input', (event) => this.clearField(event.target));
        this.form.addEventListener('change', (event) => this.clearField(event.target));
      }

      get controls() {
        return Array.from(this.form.querySelectorAll('[required]'));
      }

      onSubmit(event) {
        this.clearAll();

        const invalid = this.controls.filter((control) => !control.checkValidity());
        if (!invalid.length) return;

        event.preventDefault();
        invalid.forEach((control) => {
          this.markInvalid(control);
          this.showMessage(control);
        });
        this.focusControl(invalid[0]);
      }

      // A painted-out select is represented by its visible toggle button.
      proxyFor(control) {
        const select = control.closest('.contact-form__select.is-enhanced');
        return select ? select.querySelector('.contact-form__select-toggle') : control;
      }

      markInvalid(control) {
        const target = this.proxyFor(control);
        if (target) target.setAttribute('aria-invalid', 'true');
      }

      /*
        checkValidity() already knows whether the field is empty or holds
        something malformed, so say which rather than falling back to one line
        of copy that fits neither.
      */
      messageFor(control) {
        const validity = control.validity;
        const malformedEmail = control.type === 'email' && !validity.valueMissing && validity.typeMismatch;
        if (malformedEmail && this.emailMessage) return this.emailMessage;
        return this.message;
      }

      showMessage(control) {
        const field = control.closest('.contact-form__field');
        const element = field ? field.querySelector('.contact-form__error') : null;
        const text = this.messageFor(control);
        if (!element || !text) return;

        element.textContent = text;
        element.hidden = false;

        const target = this.proxyFor(control);
        if (target && element.id) target.setAttribute('aria-describedby', element.id);
      }

      focusControl(control) {
        const target = this.proxyFor(control);
        if (target && typeof target.focus === 'function') target.focus();
      }

      clearField(control) {
        if (!control || !control.closest) return;

        const field = control.closest('.contact-form__field');
        if (!field) return;

        field.querySelectorAll('[aria-invalid]').forEach((element) => {
          element.removeAttribute('aria-invalid');
          element.removeAttribute('aria-describedby');
        });

        const element = field.querySelector('.contact-form__error');
        if (element) element.hidden = true;
      }

      clearAll() {
        this.form.querySelectorAll('[aria-invalid]').forEach((element) => {
          element.removeAttribute('aria-invalid');
          element.removeAttribute('aria-describedby');
        });
        this.form.querySelectorAll('.contact-form__error').forEach((element) => {
          element.hidden = true;
        });
      }
    },
  );
}
